/**
 * URL state for the Companion local comparison (`/companion/local`). Tiny and dependency free,
 * so the Companion navigation (on every Companion page) can link to it cheaply.
 */
import { sportLabel } from "@/lib/conditions/sport-meta";
import { areaLinkForSport, areaPageSportFor, pageAreaSlugFor } from "@/lib/areas/links";
import { isAreaBoardSport, type AreaBoardSport } from "@/lib/areas/types";

export type LocalComparisonMode = "area" | "radius";
export type LocalComparisonSort = "score" | "distance";

export const LOCAL_RADIUS_OPTIONS_KM = [20, 40, 60, 90] as const;
export type LocalRadiusKm = (typeof LOCAL_RADIUS_OPTIONS_KM)[number];
export const DEFAULT_LOCAL_RADIUS_KM: LocalRadiusKm = 40;
export const LOCAL_COMPARISON_PATH = "/companion/local";

/** Everything the view needs is in the URL, so a view can be saved and shared. */
export type LocalComparisonParams = Readonly<{
  mode: LocalComparisonMode;
  /** Area mode: the chosen area; null means the base's own area. */
  area: string | null;
  radiusKm: LocalRadiusKm;
  /** Radius mode: a chosen origin spot; null means the base anchor. */
  from: string | null;
  /** Raw requested sport key (normalised later, with gating). */
  sport: string | null;
  sort: LocalComparisonSort;
  base: string | null;
}>;

type SearchValue = string | readonly string[] | undefined;

const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/u;
const SPORT_KEY = /^[a-z][a-z0-9-]{0,31}$/u;

function first(value: SearchValue): string | null {
  const raw = Array.isArray(value) ? value[0] : value;
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim().toLowerCase();
  return trimmed.length > 0 ? trimmed : null;
}

function slugOrNull(value: SearchValue): string | null {
  const raw = first(value);
  return raw && raw.length <= 80 && SLUG.test(raw) ? raw : null;
}

/** Parses the view's search params; anything malformed falls back to the default. */
export function parseLocalComparisonParams(
  search: Readonly<Record<string, SearchValue>>,
): LocalComparisonParams {
  const mode = first(search.mode) === "radius" ? "radius" : "area";
  const km = Number(first(search.km));
  const radiusKm = (LOCAL_RADIUS_OPTIONS_KM as readonly number[]).includes(km)
    ? (km as LocalRadiusKm)
    : DEFAULT_LOCAL_RADIUS_KM;
  const sport = first(search.sport);
  const base = first(search.base);
  return {
    mode,
    area: slugOrNull(search.area),
    radiusKm,
    from: slugOrNull(search.from),
    sport: sport && SPORT_KEY.test(sport) ? sport : null,
    sort: first(search.sort) === "distance" ? "distance" : "score",
    base: base && base.length <= 64 && /^[a-z0-9-]+$/u.test(base) ? base : null,
  };
}

/**
 * Builds "/companion/local?..." with keys in a stable order. Defaults are left out, so a saved
 * link stays short: area mode, 40 km, score order.
 */
export function localComparisonHref(
  params: Partial<Omit<LocalComparisonParams, "sport">> & Readonly<{ sport?: string | null }> = {},
): string {
  const query = new URLSearchParams();
  const mode = params.mode ?? "area";
  query.set("mode", mode);
  if (mode === "area" && params.area) query.set("area", params.area);
  if (mode === "radius") {
    if (params.radiusKm && params.radiusKm !== DEFAULT_LOCAL_RADIUS_KM) query.set("km", String(params.radiusKm));
    if (params.from) query.set("from", params.from);
  }
  if (params.sport) query.set("sport", params.sport);
  if (params.sort === "distance") query.set("sort", "distance");
  if (params.base) query.set("base", params.base);
  return `${LOCAL_COMPARISON_PATH}?${query.toString()}`;
}


const NAMED_PADDLE_KEYS = new Set(["sup-touring", "sup-surf", "sup-downwind"]);

/**
 * Maps any sport key Companion stores to a board sport the area readers accept. Named paddle
 * profiles always resolve to the generic board (the area readers never accept a named profile,
 * spec section 5.1), whatever the request's verification state.
 */
export function normaliseLocalSport(value: string | null | undefined): AreaBoardSport | null {
  const key = value?.trim().toLowerCase().replaceAll("_", "-");
  if (!key) return null;
  if (key === "ski" || key === "snow" || key === "snowboard") return "piste";
  if (key === "offpiste") return "off-piste";
  if (key === "ski-touring") return "touring";
  if (key === "swim" || key === "open-water") return "swimming";
  if (key === "sailing-club" || key === "sailing-hp") return "sailing";
  if (key === "coastal") return "surf";
  if (NAMED_PADDLE_KEYS.has(key)) return "sup";
  return isAreaBoardSport(key) ? key : null;
}

export function localSportLabel(sport: AreaBoardSport): string {
  if (sport === "sailing") return "Sailing";
  if (sport === "sup") return "SUP";
  return sportLabel(sport);
}

/**
 * Public link for an area: the area page for surf or piste when one exists (the area's own or
 * its nearest ancestor's, named after the page it opens), else the board filtered to the area.
 */
export function localPublicLink(
  areaSlug: string,
  areaName: string,
  sport: AreaBoardSport,
  nameFor: (slug: string) => string | null = () => null,
): Readonly<{ href: string; label: string }> | null {
  const href = areaLinkForSport(areaSlug, sport);
  const pageSport = areaPageSportFor(sport);
  const pageSlug = pageSport ? pageAreaSlugFor(areaSlug, pageSport) : null;
  if (pageSport && pageSlug) {
    const pageName = pageSlug === areaSlug ? areaName : nameFor(pageSlug) ?? "Regional";
    return { href, label: `${pageName} ${pageSport === "piste" ? "snow" : "surf"} forecast page` };
  }
  if (href === "/conditions") return null;
  return { href, label: `${localSportLabel(sport)} board for ${areaName}` };
}
